/**
 * Page notifications and Web Push (spec §7.5–§7.6). The service-worker message protocol
 * (`{type:"push"}` → sync INBOX, `{type:"open", url}` → navigate) lives here too.
 * `notifyNew` checks `\Seen` (the legacy compared the bare word "Seen", §21.3).
 */
import { api } from '$lib/api/client';
import { getLang, t } from '$lib/i18n';
import type { MessageRow } from '$lib/types';
import type { MailStore } from './mail.svelte';
import { LS, lsSet } from './state.svelte';

export type PushResult = 'ok' | 'failed' | 'denied' | 'unsupported';

/** base64url → bytes for `applicationServerKey`. */
export function urlBase64ToBytes(s: string): Uint8Array {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function pushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof window !== 'undefined' && 'PushManager' in window && typeof Notification !== 'undefined';
}

/** iOS (iPad/iPhone/iPod, or Mac with touch) not running as an installed PWA. */
export function needsInstall(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = (navigator as { standalone?: boolean }).standalone === true || !!window.matchMedia?.('(display-mode: standalone)').matches;
  return ios && !standalone;
}

function sameKey(sub: PushSubscription, key: string): boolean {
  const k = sub.options?.applicationServerKey;
  if (!k) return false;
  const a = new Uint8Array(k);
  const b = urlBase64ToBytes(key);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

async function localSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    return await (await navigator.serviceWorker.ready).pushManager.getSubscription();
  } catch {
    return null;
  }
}

export class PushModule {
  private swListenerBound = false;
  constructor(private readonly s: MailStore) {}

  private async status(): Promise<{ subscribed: boolean; devices: number; watching: boolean }> {
    try {
      const st = await api.pushStatus();
      return { subscribed: !!(await localSubscription()) && st.devices > 0, devices: st.devices || 0, watching: !!st.watching };
    } catch {
      return { subscribed: false, devices: 0, watching: false };
    }
  }

  private applyStatus(st: { subscribed: boolean; devices: number; watching: boolean }): void {
    this.s.pushOn = st.subscribed;
    this.s.pushDevices = st.devices;
    this.s.pushWatching = st.watching;
  }

  /** SW message listener + status read; subscribes when notifications are wanted but not subscribed. */
  async bootPush(): Promise<void> {
    this.s.canPush = pushSupported();
    this.s.needsInstall = needsInstall();
    if (!this.s.canPush) return;
    if (!this.swListenerBound) {
      this.swListenerBound = true;
      navigator.serviceWorker?.addEventListener('message', (ev: MessageEvent) => this.onSwMessage(ev.data));
    }
    const st = await this.status();
    this.applyStatus(st);
    if (!st.subscribed && this.s.notifyEnabled) await this.enablePush();
  }

  /** `{type:"push"}` → sync INBOX; `{type:"open", url}` → assign the hash part. */
  onSwMessage(data: unknown): void {
    const msg = (data || {}) as { type?: string; url?: unknown };
    if (msg.type === 'push') void this.s.syncFolder('INBOX').catch(() => undefined);
    else if (msg.type === 'open' && typeof msg.url === 'string') {
      const hash = msg.url.replace(/^.*#/, '#');
      if (hash.startsWith('#') && typeof location !== 'undefined') location.hash = hash;
    }
  }

  async enableNotifications(): Promise<'granted' | 'denied' | 'unsupported'> {
    if (typeof Notification === 'undefined') {
      this.s.notifyPerm = 'unsupported';
      return 'unsupported';
    }
    let perm = Notification.permission;
    if (perm === 'default') {
      try {
        perm = await Notification.requestPermission();
      } catch {
        perm = 'denied';
      }
    }
    this.s.notifyPerm = perm;
    this.s.notifyWanted = perm === 'granted';
    lsSet(LS.notify, this.s.notifyWanted ? '1' : '0');
    if (perm === 'granted') await this.enablePush();
    return perm === 'granted' ? 'granted' : 'denied';
  }

  async disableNotifications(): Promise<void> {
    this.s.notifyWanted = false;
    lsSet(LS.notify, '0');
    await this.disablePush();
  }

  /** Subscribe with the server's VAPID key (single-flight via `pushBusy`). */
  async enablePush(): Promise<PushResult> {
    if (this.s.pushBusy) return 'failed';
    this.s.pushBusy = true;
    try {
      const r = await this.subscribe();
      if (r === 'ok') this.applyStatus(await this.status());
      return r;
    } finally {
      this.s.pushBusy = false;
    }
  }

  private async subscribe(): Promise<PushResult> {
    if (!pushSupported()) return 'unsupported';
    if (Notification.permission !== 'granted') return 'denied';
    try {
      const { key } = await api.pushKey();
      if (!key) return 'failed';
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      // A subscription made with another VAPID key can never be delivered to: replace it.
      if (existing && !sameKey(existing, key)) await existing.unsubscribe().catch(() => false);
      const sub = existing && sameKey(existing, key) ? existing : await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(key) as BufferSource });
      await api.pushSubscribe(sub.toJSON(), getLang(), navigator.userAgent);
      return 'ok';
    } catch (err) {
      return (err as { name?: string })?.name === 'NotAllowedError' ? 'denied' : 'failed';
    }
  }

  async disablePush(): Promise<void> {
    this.s.pushBusy = true;
    try {
      const sub = await localSubscription();
      if (sub) {
        await api.pushUnsubscribe(sub.endpoint).catch(() => null);
        await sub.unsubscribe().catch(() => false);
      }
      this.applyStatus(await this.status());
    } finally {
      this.s.pushBusy = false;
    }
  }

  /** POST /api/push/test → raw toast text for 4 s (bypasses the i18n toast timer). */
  async testPush(): Promise<{ sent: number } | null> {
    let r: { sent: number } | null = null;
    try {
      r = await api.pushTest();
    } catch {
      r = null;
    }
    this.s.showToast(r && r.sent > 0 ? t('pushTestSent') : t('pushTestFailed'), false, 4000);
    return r;
  }

  /**
   * Page notifications for new INBOX rows above `notifyMark` that are unseen (`\Seen`), only
   * while the document is hidden: the last 3 individually, plus a summary when more.
   * Returns how many notifications were shown.
   */
  notifyNew(rows: readonly MessageRow[]): number {
    if (!this.s.notifyEnabled) return 0;
    const mark = this.s.notifyMark;
    const fresh = rows.filter((r) => r.folder === 'INBOX' && r.uid > mark && !r.flags.includes('\\Seen')).sort((a, b) => a.uid - b.uid);
    const maxUid = rows.reduce((m, r) => (r.folder === 'INBOX' && r.uid > m ? r.uid : m), mark);
    if (maxUid > mark) this.s.setNotifyMark(maxUid);
    if (!fresh.length || (typeof document !== 'undefined' && !document.hidden)) return 0;
    const shown = fresh.slice(-3);
    for (const r of shown) {
      try {
        const n = new Notification(r.fromName || r.fromAddr || t('newMail'), { body: r.subject || t('noSubject'), tag: r.key, icon: '/img/icon-512.png', silent: false });
        n.onclick = () => {
          window.focus();
          void this.s.openMessage(r.uid, 'INBOX');
          n.close();
        };
      } catch {
        /* notification constructor can throw on some platforms */
      }
    }
    if (fresh.length > shown.length) {
      try {
        new Notification(t('newMail'), { body: t('msgsN', { n: fresh.length }), tag: 'tik-more' });
      } catch {
        /* ignore */
      }
    }
    return shown.length;
  }

  /** Title + app badge from the INBOX unseen count (spec §3). */
  applyBadge(unseen: number): void {
    if (typeof document !== 'undefined') document.title = unseen > 0 ? `(${unseen}) ${t('appName')}` : t('appName');
    try {
      if (unseen > 0) void navigator.setAppBadge?.(unseen)?.catch(() => undefined);
      else void navigator.clearAppBadge?.()?.catch(() => undefined);
    } catch {
      /* unsupported */
    }
  }
}
